import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrophy,
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faEye,
  faEyeSlash,
  faArrowUpRightFromSquare,
  faBell,
  faBullhorn,
  faRotateLeft,
  faTriangleExclamation,
  faRotate,
  faCircleCheck,
  faChevronDown,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import Participants from '../components/Competitions/Participants';
import './Competitions.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const LEVELS = [
  ['all', 'All students'],
  ['little', 'Little (6–8)'],
  ['junior', 'Junior (9+)'],
];
const STATUS = {
  upcoming: ['Coming soon', 'is-info'],
  open: ['Open for projects', 'is-good'],
  judging: ['Teachers voting', 'is-warn'],
  finished: ['Winners published', 'is-off'],
};
const day = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
};
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
// new challenge: this month, from today to the last day of the month
const newDraft = () => {
  const now = new Date();
  return {
    id: null,
    title: `${now.toLocaleDateString('en-US', { month: 'long' })} challenge: `,
    level: 'all',
    startDate: iso(now),
    endDate: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    prize: 'Winner badge on the dashboard + shout-out on our Instagram',
    description: '',
  };
};

/* Top 3 by average score (more votes first on a tie) */
const suggestRanks = (entries) => {
  const out = {};
  entries
    .filter((e) => !e.hidden && e.avg != null)
    .sort((a, b) => b.avg - a.avg || b.votes - a.votes)
    .slice(0, 3)
    .forEach((e, i) => {
      out[e.id] = i + 1;
    });
  return out;
};

const CompetitionRow = ({ c, activeTeachers, onEdit, onChanged, say }) => {
  const [open, setOpen] = useState(c.status === 'judging' || c.status === 'open');
  const [ranks, setRanks] = useState(() => (c.status === 'finished' ? Object.fromEntries(c.entries.filter((e) => e.rank).map((e) => [e.id, e.rank])) : suggestRanks(c.entries)));
  const [busy, setBusy] = useState(false);
  const [label, tone] = STATUS[c.status] || STATUS.upcoming;
  const sorted = [...c.entries].sort((a, b) => (b.avg ?? -1) - (a.avg ?? -1) || b.votes - a.votes);

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) say(msg);
      onChanged();
    } catch (err) {
      say(err.response?.data?.error || 'Something went wrong.', true);
    } finally {
      setBusy(false);
    }
  };

  const setRank = (entryId, rank) =>
    setRanks((r) => {
      const n = { ...r };
      Object.keys(n).forEach((k) => {
        if (rank && n[k] === rank) delete n[k]; // one entry per place
      });
      if (rank) n[entryId] = rank;
      else delete n[entryId];
      return n;
    });

  return (
    <li className={`rmacmp-item ${tone}`}>
      <div className="rmacmp-item-head">
        <button type="button" className="rmacmp-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <FontAwesomeIcon icon={faChevronDown} className={open ? 'is-open' : ''} />
          <span>
            <b>{c.title}</b>
            <small>
              {LEVELS.find(([k]) => k === c.level)?.[1]} · {day(c.startDate)} → {day(c.endDate)} · {c.entries.length} {c.entries.length === 1 ? 'project' : 'projects'}
            </small>
          </span>
        </button>
        <span className="rmacmp-pill">{label}</span>
        <div className="rmacmp-actions">
          <button type="button" onClick={() => onEdit(c)} aria-label="Edit challenge" title="Edit">
            <FontAwesomeIcon icon={faPen} />
          </button>
          <button
            type="button"
            className="is-danger"
            aria-label="Delete challenge"
            title="Delete"
            disabled={busy}
            onClick={() =>
              window.confirm(`Delete “${c.title}” and its ${c.entries.length} projects?`) &&
              run(() => axios.delete(`${API}/api/admin/competitions/${c.id}`, auth()), 'Challenge deleted.')
            }
          >
            <FontAwesomeIcon icon={faTrashCan} />
          </button>
        </div>
      </div>

      {open && (
        <div className="rmacmp-body">
          {c.description && <p className="rmacmp-desc">{c.description}</p>}

          {c.participants?.length > 0 && (
            <div className="rmacmp-people">
              <h4>
                {c.participants.length} taking part · {c.participants.filter((p) => p.sent).length} projects sent
              </h4>
              <Participants list={c.participants} />
            </div>
          )}

          {c.entries.length === 0 ? (
            <p className="rmacmp-dim">{c.status === 'upcoming' ? 'Students can send projects from the start date.' : 'No projects yet.'}</p>
          ) : (
            <div className="rmacmp-table-wrap">
              <table className="rmacmp-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Project</th>
                    <th>Score</th>
                    <th>Place</th>
                    <th aria-label="Show or hide" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((e) => (
                    <tr key={e.id} className={e.hidden ? 'is-hidden' : ''}>
                      <td>
                        <a href={`/admin/StudentProfile/${e.studentId}`} target="_blank" rel="noopener noreferrer">
                          {e.name}
                        </a>
                      </td>
                      <td>
                        <a href={e.link} target="_blank" rel="noopener noreferrer" className="rmacmp-link">
                          {e.title || 'Open project'} <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                        </a>
                        {e.note && <small>{e.note}</small>}
                      </td>
                      <td className="rmacmp-num">
                        {e.avg != null ? <b>{e.avg}</b> : '—'}
                        <small>
                          {e.votes}/{activeTeachers} votes
                        </small>
                      </td>
                      <td>
                        <select value={ranks[e.id] || ''} onChange={(ev) => setRank(e.id, parseInt(ev.target.value, 10) || null)} disabled={e.hidden || busy} aria-label={`Place for ${e.name}`}>
                          <option value="">—</option>
                          <option value="1">🥇 1st</option>
                          <option value="2">🥈 2nd</option>
                          <option value="3">🥉 3rd</option>
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="rmacmp-icon"
                          onClick={() => run(() => axios.put(`${API}/api/admin/competitions/entry/${e.id}`, { hidden: !e.hidden }, auth()), e.hidden ? 'Project shown.' : 'Project hidden.')}
                          disabled={busy}
                          aria-label={e.hidden ? 'Show project' : 'Hide project'}
                          title={e.hidden ? 'Hidden from students and teachers: show it' : 'Hide from students and teachers'}
                        >
                          <FontAwesomeIcon icon={e.hidden ? faEyeSlash : faEye} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="rmacmp-foot">
            {(c.status === 'upcoming' || c.status === 'open') && (
              <Link className="rmacmp-btn rmacmp-btn--ghost" to={`/Admin/sendemails?competition=${c.id}`}>
                <FontAwesomeIcon icon={faEnvelope} /> Email all parents
              </Link>
            )}
            {c.status === 'judging' && (
              <button type="button" className="rmacmp-btn rmacmp-btn--ghost" disabled={busy} onClick={() => run(() => axios.post(`${API}/api/admin/competitions/${c.id}/notify-teachers`, {}, auth()), 'Teachers notified.')}>
                <FontAwesomeIcon icon={faBell} /> Ask teachers to vote
              </button>
            )}
            {(c.status === 'judging' || c.status === 'finished') && c.entries.length > 0 && (
              <button
                type="button"
                className="rmacmp-btn"
                disabled={busy || !Object.values(ranks).includes(1)}
                onClick={() => run(() => axios.post(`${API}/api/admin/competitions/${c.id}/publish`, { ranks }, auth()), 'Winners published! Everyone can see them now.')}
                title={!Object.values(ranks).includes(1) ? 'Pick at least the 1st place' : ''}
              >
                <FontAwesomeIcon icon={faBullhorn} /> {c.status === 'finished' ? 'Update winners' : 'Publish winners'}
              </button>
            )}
            {c.status === 'finished' && (
              <button type="button" className="rmacmp-btn rmacmp-btn--ghost" disabled={busy} onClick={() => run(() => axios.post(`${API}/api/admin/competitions/${c.id}/unpublish`, {}, auth()), 'Winners hidden again.')}>
                <FontAwesomeIcon icon={faRotateLeft} /> Unpublish
              </button>
            )}
            {c.status === 'open' && <p className="rmacmp-dim">Projects stay private until the end date. Then teachers can vote.</p>}
          </div>
        </div>
      )}
    </li>
  );
};

const Competitions = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/api/admin/competitions`, auth());
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'We couldn’t load the competitions.');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (!draft) return undefined;
    const onKey = (e) => e.key === 'Escape' && !saving && setDraft(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [draft, saving]);

  const say = (message, bad = false) => setToast({ message, bad });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      if (draft.id) await axios.put(`${API}/api/admin/competitions/${draft.id}`, draft, auth());
      else await axios.post(`${API}/api/admin/competitions`, draft, auth());
      setDraft(null);
      say(draft.id ? 'Challenge saved.' : 'Challenge created. Students will see it on the start date.');
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Couldn’t save the challenge.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !data) {
    return (
      <div className="rmacmp">
        <div className="rmacmp-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <p>{error}</p>
          <button type="button" className="rmacmp-btn" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data) return <div className="rmacmp" aria-busy="true"><div className="rmacmp-skel" /></div>;

  const comps = data.competitions || [];

  return (
    <div className="rmacmp">
      <header className="rmacmp-header">
        <div>
          <h1>Competitions</h1>
          <p>One challenge a month. Students send a project link while it’s open; after the end date every teacher scores the projects 1–10, then you publish the top 3. Students are shown as first name + last initial.</p>
        </div>
        <button type="button" className="rmacmp-btn" onClick={() => { setFormError(''); setDraft(newDraft()); }}>
          <FontAwesomeIcon icon={faPlus} /> New challenge
        </button>
      </header>

      {comps.length === 0 ? (
        <div className="rmacmp-empty">
          <FontAwesomeIcon icon={faTrophy} />
          <p>No challenges yet. Create the first one: students and teachers see it on their Competitions page.</p>
        </div>
      ) : (
        <ul className="rmacmp-list">
          {comps.map((c) => (
            <CompetitionRow
              key={`${c.id}-${c.status}-${c.entries.length}`}
              c={c}
              activeTeachers={data.activeTeachers}
              onEdit={(x) => { setFormError(''); setDraft({ id: x.id, title: x.title, level: x.level, startDate: x.startDate, endDate: x.endDate, prize: x.prize, description: x.description }); }}
              onChanged={load}
              say={say}
            />
          ))}
        </ul>
      )}

      {draft && (
        <div className="rmacmp-overlay" onClick={() => !saving && setDraft(null)}>
          <form className="rmacmp-modal" role="dialog" aria-modal="true" aria-label="Challenge" onClick={(e) => e.stopPropagation()} onSubmit={save}>
            <header>
              <h2>
                <FontAwesomeIcon icon={faTrophy} /> {draft.id ? 'Edit challenge' : 'New challenge'}
              </h2>
              <button type="button" className="rmacmp-x" onClick={() => setDraft(null)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </header>
            <div className="rmacmp-modal-body">
              <label className="rmacmp-field">
                <span>Title</span>
                <input type="text" maxLength={120} value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} required disabled={saving} />
              </label>
              <div className="rmacmp-three">
                <label className="rmacmp-field">
                  <span>For</span>
                  <select value={draft.level} onChange={(e) => setDraft({ ...draft, level: e.target.value })} disabled={saving}>
                    {LEVELS.map(([k, l]) => (
                      <option key={k} value={k}>
                        {l}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="rmacmp-field">
                  <span>Starts</span>
                  <input type="date" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} required disabled={saving} />
                </label>
                <label className="rmacmp-field">
                  <span>Last day to send</span>
                  <input type="date" value={draft.endDate} min={draft.startDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} required disabled={saving} />
                </label>
              </div>
              <label className="rmacmp-field">
                <span>The challenge</span>
                <textarea
                  rows={6}
                  maxLength={2000}
                  placeholder={'Make a game with a score counter.\n\nIdeas: catch the falling stars, a space shooter, a quiz…\nRules: your own project, made this month. Scratch, Python or any tool you learned.'}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  disabled={saving}
                />
              </label>
              <label className="rmacmp-field">
                <span>Prize (optional)</span>
                <input type="text" maxLength={200} value={draft.prize} onChange={(e) => setDraft({ ...draft, prize: e.target.value })} disabled={saving} />
              </label>
              {formError && <p className="rmacmp-error">{formError}</p>}
            </div>
            <footer>
              <button type="button" className="rmacmp-btn rmacmp-btn--ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmacmp-btn" disabled={saving}>
                {saving ? 'Saving…' : draft.id ? 'Save' : 'Create challenge'}
              </button>
            </footer>
          </form>
        </div>
      )}

      {toast && (
        <div className={`rmacmp-toast ${toast.bad ? 'is-bad' : ''}`} role="status">
          <FontAwesomeIcon icon={toast.bad ? faTriangleExclamation : faCircleCheck} /> {toast.message}
        </div>
      )}
    </div>
  );
};

export default Competitions;
